import { ReactElement } from "react";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/LandingLayout";
import routes from "@/constants/routes";
import { getArticles } from "@/services/cms/api";
import { ARTICLES_DEFAULT_POPULATE } from "@/components/blog/articleList/ArticleList";
import { Article } from "@/services/cms/apiTypes";
import { SubLayout } from "@/containers/layout/LandingLayout";
import getConfig from "next/config";
import { useWindowSize } from "@/shared/hooks/useWindowSize";
import { ArticleContent } from "@/components/blog/articleContent/articleContent";

const { publicRuntimeConfig } = getConfig();

function formatISODateToReadableDate(inputDateString: string) {
  const date = new Date(inputDateString);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  const day = date.getDate();

  const formattedDateString = `${monthNames[monthIndex]} ${day}, ${year}`;

  return formattedDateString;
}

interface BlogProps {
  article: Article;
}
const BlogPage = ({ article }: BlogProps): JSX.Element => {
  const nextArticle = article.attributes.nextArticle;
  const { width } = useWindowSize();

  return (
    <SubLayout allowOverflow>
      <Head>
        <title>{article.attributes.title}</title>
        <meta name="description" content={article.attributes.description} />
        <meta name="twitter:card" content="Keyzy" key="twcard" />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content={article.attributes.slug} key="ogurl" />
        <meta property="og:type" content="article" key="ogtype" />
        <meta
          property="og:image"
          content={article?.attributes?.cover?.data?.attributes?.formats?.large?.url}
          key="ogimage"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta property="og:title" content={article.attributes.title} key="ogtitle" />
        <meta property="og:description" content={article.attributes.description} key="ogdesc" />
        <meta property="og:locale" content="en_GB" />
      </Head>
      <ArticleContent article={article} baseUrl={routes.blog}></ArticleContent>
    </SubLayout>
  );
};

BlogPage.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout theme="light">{page}</MainLayout>;
};
export async function getServerSideProps(context: any) {
  if (publicRuntimeConfig.NEXT_BLOG_PAGE !== "1") {
    return {
      notFound: true,
    };
  }
  const { slug } = context.query;
  const articleResponse = await getArticles({
    populate: [
      ...ARTICLES_DEFAULT_POPULATE,
      "nextArticle",
      "nextArticle.cover",
      "nextArticle.author",
      "nextArticle.author.avatar",
    ],
    filters: {
      slug: {
        $eq: slug,
      },
    },
  });
  if (articleResponse.data.length === 1) {
    return {
      props: { article: articleResponse.data[0] },
    };
  }
  return {
    notFound: true,
  };
}
export default BlogPage;
